import { buildAdminRoute } from '@/constants/security';
// admin-notifications.helpers.ts

import type {
  AdminNotificationActorSnapshot,
  AdminNotificationChangedField,
  AdminNotificationClientSnapshot,
  AdminNotificationDestinationCode,
  AdminNotificationDestinationParams,
  AdminNotificationEventCode,
  AdminNotificationItem,
  AdminNotificationPayload,
  AdminNotificationPriorityCode,
  AdminNotificationPropertySnapshot,
  AdminNotificationRouteParams,
  AdminNotificationSemanticClassCode,
  AdminNotificationSubjectSnapshot,
  AdminNotificationNavigationSource,
  AdminNotificationsFeedFilters,
  ISODateTimeString,
  JSONObject,
  RpcAdminNotificationsFeedInput,
  RpcAdminNotificationsFeedRow,
} from './admin-notifications.types';

const EVENT_LABELS_ES: Record<string, string> = {
  visit_booking_created: 'Nueva visita',
  visit_booking_updated: 'Visita actualizada',
  visit_booking_status_changed: 'Cambio de estado',
  crm_visit_scheduled_external: 'Visita agendada',
  crm_visit_scheduled_internal: 'Visita interna',
};

const PRIORITY_LABELS_ES: Record<string, string> = {
  low: 'Baja',
  normal: 'Normal',
  high: 'Alta',
  critical: 'CrÃ­tica',
};

const SEMANTIC_CLASS_LABELS_ES: Record<string, string> = {
  audit: 'AuditorÃ­a',
  notice: 'Aviso',
};

const CHANGED_FIELD_LABELS_ES: Record<AdminNotificationChangedField, string> = {
  property_id: 'Propiedad',
  visitor_name: 'Visitante',
  user_email: 'Correo electrÃ³nico',
  whatsapp_number: 'WhatsApp',
  visit_type: 'Tipo de visita',
  scheduled_date: 'Fecha',
  scheduled_time: 'Hora',
  planned_move_in_date: 'Mudanza estimada',
  notes: 'Notas',
  status: 'Estado',
  digital_card_id: 'Agente',
  operation_type: 'OperaciÃ³n',
};

const VISITS_ROUTE_ALIASES = new Set([
  '/admin/visitas',
  '/admin/visits',
  'admin/visitas',
  'admin/visits',
  '/visitas',
  '/visits',
  'visitas',
  'visits',
]);

const CLIENTS_ROUTE_ALIASES = new Set([
  '/admin/clientes',
  '/admin/clients',
  'admin/clientes',
  'admin/clients',
  '/clientes',
  '/clients',
  'clientes',
  'clients',
]);

function isJSONObject(value: unknown): value is JSONObject {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function readNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function toRouteParams(value: unknown): AdminNotificationRouteParams {
  if (!isJSONObject(value)) {
    return {};
  }

  return {
    ...value,
    tab: typeof value.tab === 'string' ? value.tab : undefined,
    visitBookingId:
      typeof value.visitBookingId === 'string' ? value.visitBookingId : undefined,
    legacyBookingId:
      typeof value.legacyBookingId === 'string' ? value.legacyBookingId : undefined,
    crmVisitId:
      typeof value.crmVisitId === 'string' ? value.crmVisitId : undefined,
    visitId:
      typeof value.visitId === 'string' ? value.visitId : undefined,
    clientId:
      typeof value.clientId === 'string' ? value.clientId : undefined,
    propertyId: typeof value.propertyId === 'string' ? value.propertyId : undefined,
    status: typeof value.status === 'string' ? value.status : undefined,
    q: typeof value.q === 'string' ? value.q : undefined,
  };
}

function toDestinationParams(value: unknown): AdminNotificationDestinationParams {
  if (!isJSONObject(value)) {
    return {};
  }

  return {
    ...value,
    legacyBookingId:
      typeof value.legacyBookingId === 'string' ? value.legacyBookingId : undefined,
    visitBookingId:
      typeof value.visitBookingId === 'string' ? value.visitBookingId : undefined,
    crmVisitId:
      typeof value.crmVisitId === 'string' ? value.crmVisitId : undefined,
    visitId:
      typeof value.visitId === 'string' ? value.visitId : undefined,
    clientId:
      typeof value.clientId === 'string' ? value.clientId : undefined,
    propertyId: typeof value.propertyId === 'string' ? value.propertyId : undefined,
    status: typeof value.status === 'string' ? value.status : undefined,
    tab: typeof value.tab === 'string' ? value.tab : undefined,
    q: typeof value.q === 'string' ? value.q : undefined,
  };
}

function toPayload(value: unknown): AdminNotificationPayload {
  if (!isJSONObject(value)) {
    return {};
  }

  return value as AdminNotificationPayload;
}

function toChangedFields(value: unknown): AdminNotificationChangedField[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(
    (field): field is AdminNotificationChangedField => typeof field === 'string',
  );
}

function toActorSnapshot(value: unknown): AdminNotificationActorSnapshot {
  if (!isJSONObject(value)) {
    return {};
  }

  return value as AdminNotificationActorSnapshot;
}

function toPropertySnapshot(value: unknown): AdminNotificationPropertySnapshot {
  if (!isJSONObject(value)) {
    return {};
  }

  return value as AdminNotificationPropertySnapshot;
}

function toClientSnapshot(value: unknown): AdminNotificationClientSnapshot {
  if (!isJSONObject(value)) {
    return {};
  }

  return value as AdminNotificationClientSnapshot;
}

function toSubjectSnapshot(value: unknown): AdminNotificationSubjectSnapshot {
  if (!isJSONObject(value)) {
    return {};
  }

  return value as AdminNotificationSubjectSnapshot;
}

function normalizeAdminNotificationDestinationCode(
  destinationCode: unknown,
): AdminNotificationDestinationCode | null {
  const normalized = readString(destinationCode);
  return normalized ?? null;
}

function toDestinationRouteParams(
  destinationCode: AdminNotificationDestinationCode | null,
  destinationParams: AdminNotificationDestinationParams,
): { path: string; params: AdminNotificationRouteParams } | null {
  if (destinationCode === 'admin_crm_visit') {
    return {
      path: buildAdminRoute('clients'),
      params: {
        status: readString(destinationParams.status) ?? undefined,
        q: readString(destinationParams.q) ?? undefined,
        visitId:
          readString(destinationParams.visitId)
          ?? readString(destinationParams.crmVisitId)
          ?? undefined,
        clientId: readString(destinationParams.clientId) ?? undefined,
        propertyId: readString(destinationParams.propertyId) ?? undefined,
      },
    };
  }

  if (destinationCode === 'legacy_admin_visit_booking') {
    return {
      path: buildAdminRoute('visits'),
      params: {
        tab: readString(destinationParams.tab) ?? 'visitas',
        visitBookingId:
          readString(destinationParams.visitBookingId)
          ?? readString(destinationParams.legacyBookingId)
          ?? undefined,
        legacyBookingId: readString(destinationParams.legacyBookingId) ?? undefined,
        crmVisitId: readString(destinationParams.crmVisitId) ?? undefined,
        propertyId: readString(destinationParams.propertyId) ?? undefined,
      },
    };
  }

  return null;
}

export function resolveAdminNotificationNavigationTarget(params: {
  destinationCode?: AdminNotificationDestinationCode | null;
  destinationParams?: AdminNotificationDestinationParams;
  routePath?: string | null;
  routeParams?: AdminNotificationRouteParams;
}): {
  href: string;
  routePath: string;
  routeParams: AdminNotificationRouteParams;
  navigationSource: AdminNotificationNavigationSource;
} {
  const fallbackPath = normalizeAdminNotificationRoutePath(params.routePath ?? '');
  const fallbackRouteParams = toRouteParams(params.routeParams ?? {});
  const destination = toDestinationRouteParams(
    params.destinationCode ?? null,
    params.destinationParams ?? {},
  );

  if (destination) {
    return {
      href: buildAdminNotificationHref(destination.path, destination.params),
      routePath: destination.path,
      routeParams: destination.params,
      navigationSource: 'destination',
    };
  }

  return {
    href: buildAdminNotificationHref(fallbackPath, fallbackRouteParams),
    routePath: fallbackPath,
    routeParams: fallbackRouteParams,
    navigationSource: 'route_fallback',
  };
}

export function getAdminNotificationEventLabelEs(
  eventCode: AdminNotificationEventCode | string,
): string {
  return EVENT_LABELS_ES[eventCode] ?? 'NotificaciÃ³n operativa';
}

export function getAdminNotificationPriorityLabelEs(
  priorityCode: AdminNotificationPriorityCode | string | null | undefined,
): string {
  if (!priorityCode) {
    return 'Normal';
  }

  return PRIORITY_LABELS_ES[priorityCode] ?? priorityCode;
}

export function getAdminNotificationSemanticClassLabelEs(
  semanticClassCode: AdminNotificationSemanticClassCode | string | null | undefined,
): string {
  if (!semanticClassCode) {
    return 'AuditorÃ­a';
  }

  return SEMANTIC_CLASS_LABELS_ES[semanticClassCode] ?? semanticClassCode;
}

export function getAdminNotificationChangedFieldLabelEs(
  field: AdminNotificationChangedField | string,
): string {
  if (field in CHANGED_FIELD_LABELS_ES) {
    return CHANGED_FIELD_LABELS_ES[field as AdminNotificationChangedField];
  }

  return field.replace(/_/g, ' ');
}

export function normalizeAdminNotificationRoutePath(routePath: string): string {
  const normalized = routePath.trim();

  if (!normalized) {
    return buildAdminRoute('visits');
  }

  if (/^https?:\/\//i.test(normalized)) {
    return normalized;
  }

  const compact = normalized.replace(/\/+$/, '');
  const lower = compact.toLowerCase();

  if (VISITS_ROUTE_ALIASES.has(lower)) {
    return buildAdminRoute('visits');
  }

  if (CLIENTS_ROUTE_ALIASES.has(lower)) {
    return buildAdminRoute('clients');
  }

  if (lower.startsWith('/admin/')) {
    const suffix = compact
      .slice('/admin/'.length)
      .replace(/^visitas$/i, 'visits')
      .replace(/^clientes$/i, 'clients');
    return buildAdminRoute(suffix);
  }

  if (compact.startsWith('/')) {
    return compact;
  }

  return buildAdminRoute(compact.replace(/^visitas$/i, 'visits'));
}

export function buildAdminNotificationHref(
  routePath: string,
  routeParams: AdminNotificationRouteParams,
): string {
  const basePath = normalizeAdminNotificationRoutePath(routePath);
  const params = new URLSearchParams();

  Object.entries(routeParams).forEach(([key, value]) => {
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      params.set(key, String(value));
    }
  });

  const queryString = params.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
}

export function toAdminNotificationsFeedRpcInput(
  filters: AdminNotificationsFeedFilters = {},
): RpcAdminNotificationsFeedInput {
  return {
    p_limit: filters.limit,
    p_cursor_created_at: filters.cursorCreatedAt ?? null,
    p_cursor_notification_id: filters.cursorNotificationId ?? null,
    p_only_unread: filters.onlyUnread,
  };
}

export function toAdminNotificationItem(
  row: RpcAdminNotificationsFeedRow,
): AdminNotificationItem {
  const destinationCode = normalizeAdminNotificationDestinationCode(row.destination_code);
  const destinationParams = toDestinationParams(row.destination_params ?? {});
  const routeParams = toRouteParams(row.route_params ?? {});
  const changedFields = toChangedFields(row.changed_fields ?? []);
  const payload = toPayload(row.payload ?? {});
  const actorSnapshot = toActorSnapshot(row.actor_snapshot ?? {});
  const propertySnapshot = toPropertySnapshot(row.property_snapshot ?? {});
  const clientSnapshot = toClientSnapshot(row.client_snapshot ?? {});
  const subjectSnapshot = toSubjectSnapshot(row.subject_snapshot ?? {});
  const navigation = resolveAdminNotificationNavigationTarget({
    destinationCode,
    destinationParams,
    routePath: row.route_path,
    routeParams,
  });
  const isRead = Boolean(row.is_read ?? row.read_at);
  const fallbackImageUrl = readString(propertySnapshot.primary_image_url);
  const semanticClassCode = readString(row.semantic_class_code) ?? 'audit';
  const priorityCode = readString(row.priority_code) ?? 'normal';
  const scopeCode = readString(row.scope_code) ?? 'legacy_admin_global';
  const scopeTeamId = readString(row.scope_team_id);
  const destinationVersion = readNumber(row.destination_version) ?? 1;

  return {
    id: row.notification_id,
    eventCode: row.event_code,
    eventLabelEs: getAdminNotificationEventLabelEs(row.event_code),
    semanticClassCode,
    priorityCode,
    scopeCode,
    scopeTeamId,
    crmVisitId: readString(row.crm_visit_id),
    legacyBookingId: readString(row.legacy_booking_id),
    title: row.title,
    body: row.body,
    imageUrl: row.image_url ?? fallbackImageUrl,
    destinationCode,
    destinationVersion,
    destinationParams,
    routePath: navigation.routePath,
    routeParams: navigation.routeParams,
    href: navigation.href,
    navigationSource: navigation.navigationSource,
    ctaLabel: row.cta_label,
    changedFields,
    changedFieldLabelsEs: changedFields.map(getAdminNotificationChangedFieldLabelEs),
    payload,
    actorSnapshot,
    propertySnapshot,
    clientSnapshot,
    subjectSnapshot,
    createdAt: row.created_at,
    deliveredAt: row.delivered_at,
    readAt: row.read_at,
    dismissedAt: row.dismissed_at ?? null,
    isRead,
    isUnread: !isRead,
  };
}

export function formatAdminNotificationRelativeTime(
  value: ISODateTimeString,
  locale = 'es-MX',
  nowMs = Date.now(),
): string {
  const targetMs = Date.parse(value);

  if (Number.isNaN(targetMs)) {
    return '';
  }

  const diffSeconds = Math.round((targetMs - nowMs) / 1000);
  const absoluteSeconds = Math.abs(diffSeconds);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (absoluteSeconds < 60) {
    return rtf.format(diffSeconds, 'second');
  }

  const diffMinutes = Math.round(diffSeconds / 60);
  const absoluteMinutes = Math.abs(diffMinutes);

  if (absoluteMinutes < 60) {
    return rtf.format(diffMinutes, 'minute');
  }

  const diffHours = Math.round(diffMinutes / 60);
  const absoluteHours = Math.abs(diffHours);

  if (absoluteHours < 24) {
    return rtf.format(diffHours, 'hour');
  }

  const diffDays = Math.round(diffHours / 24);
  const absoluteDays = Math.abs(diffDays);

  if (absoluteDays < 7) {
    return rtf.format(diffDays, 'day');
  }

  const diffWeeks = Math.round(diffDays / 7);
  const absoluteWeeks = Math.abs(diffWeeks);

  if (absoluteWeeks < 5) {
    return rtf.format(diffWeeks, 'week');
  }

  const diffMonths = Math.round(diffDays / 30);
  const absoluteMonths = Math.abs(diffMonths);

  if (absoluteMonths < 12) {
    return rtf.format(diffMonths, 'month');
  }

  return rtf.format(Math.round(diffDays / 365), 'year');
}

